import window from 'global/window';
import document from 'global/document';
import { getStreamingProperties, getMediaType } from './utils';

const params = new window.URLSearchParams(window.location.search);
const videoURL = params.get('v');
const videoUniqueMediaId = params.get('media');

const wrapper = document.getElementById('wrapper');
const isLocalStorageAvailable = () => {
    try {
        if (!window.localStorage) {
            return false;
        }
        const testKey = 'isLocalStorageAvailableTestKey';
        try {
            window.localStorage.setItem(testKey, 'test');
            window.localStorage.removeItem(testKey);
            return true;
        } catch {
            return false;
        }
    } catch {
        return false;
    }
};

if (videoURL) {
    const hasLocalStorage = isLocalStorageAvailable();
    const videoNode = document.createElement('video');
    videoNode.setAttribute('class', 'video-js vjs-big-play-centered');
    videoNode.setAttribute('playsinline', '');
    videoNode.setAttribute('crossorigin', 'anonymous');

    const regex = /(?:\.([^.]+))?$/;
    const fileExtension = regex.exec(videoURL)[1];
    const type = getMediaType(fileExtension);

    const options = {
        autoplay: false,
        controls: true,
        preload: 'auto',
        responsive: true,
        controlBar: {
            pictureInPictureToggle: false,
        },
        html5: {
            vhs: {
                useBandwidthFromLocalStorage: hasLocalStorage,
                limitRenditionByPlayerDimensions: false,
            },
        },
        sources: [{ src: videoURL, type }],
    };

    getStreamingProperties(videoUniqueMediaId)
        .then((response) => {
            if (response.subtitles) {
                response.subtitles.forEach((subtitle) => {
                    const trackElement = document.createElement('track');
                    trackElement.kind = 'subtitles';
                    trackElement.src = subtitle.url;
                    trackElement.label = subtitle.label;
                    videoNode.appendChild(trackElement);
                });
            }

            if(response.custom_thumbnail?.length) {
                options.poster = response.custom_thumbnail[0].url;
            } else if (response.thumbnail_url_ssl?.['1600px']) {    
                options.poster = response.thumbnail_url_ssl['1600px'];
            }

            options.muted = response?.stream?.mute ?? false;
            options.autoplay = response?.stream?.autoplay ? 'muted' : false;
            options.playsinline = response?.stream?.autoplay ? true : false;
        })
        .catch(() => {
            // No subtitles
        })
        .finally(() => {
            wrapper.appendChild(videoNode);
            const player = window.videojs(videoNode, options);

            player.on('error', () => {
                console.warn('Video.js error:', player.error());
            });

            // Autoplay safety net: video.js's built-in `autoplay: "muted"` occasionally
            // races with the browser's autoplay policy and silently misses. If the
            // player is still paused by the time metadata arrives, trigger play()
            // ourselves (with a muted retry if the browser blocks it).
            if (options.autoplay) {
                let didAttempt = false;
                const attemptPlay = () => {
                    if (didAttempt || !player.paused()) return;
                    didAttempt = true;

                    const playPromise = player.play();
                    if (playPromise && typeof playPromise.catch === 'function') {
                        playPromise.catch((error) => {
                            console.warn('Autoplay was blocked, retrying muted:', error);
                            player.muted(true);
                            const retry = player.play();
                            if (retry && typeof retry.catch === 'function') {
                                retry.catch((err) => {
                                    console.warn('Muted autoplay was also blocked:', err);
                                });
                            }
                        });
                    }
                };

                if (player.readyState() >= 1) {
                    attemptPlay();
                } else {
                    player.one('loadedmetadata', attemptPlay);
                    player.one('canplay', attemptPlay);
                }
            }
        });
} else {
    const textnode = document.createTextNode('No video URL provided');
    wrapper.appendChild(textnode);
}
